/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getStylist } from 'src/core/themes/luisStylist';
import { getOrDefaultRegionFromResource } from 'src/core/utils/csAccountUtils';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { ConversationPublishSlotStoreData } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { PublishSlotCard } from 'src/luis/projects/publishPane/components/PublishSlotCard';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';

export type PublishModalDto = {
    /**
     * The data to display for the staging slot.
     */
    stagingData: ConversationPublishSlotStoreData;

    /**
     * The data to display for the production slot.
     */
    productionData: ConversationPublishSlotStoreData;
};

const messages = defineMessages({
    modalTitle: {
        id: 'publishPane.publishPanelModal.modalTitle',
        defaultMessage: 'Choose your publishing slot and settings'
    },
    stagingSlot: {
        id: 'publishPane.publishPanelModal.stagingSlot',
        defaultMessage: 'Staging slot'
    },
    productionSlot: {
        id: 'publishPane.publishPanelModal.productionSlot',
        defaultMessage: 'Production slot'
    }
});

const { styleComponent } = getStylist('PublishPanelModal');
const Option = styleComponent<IStackProps>(Stack)('Option', theme => ({
    paddingLeft: theme.optionPaddingLeft,
    marginBottom: theme.optionMarginBottom
}));
const OptionText = styleComponent<LuisTextProps>(LuisText)('OptionText', theme => ({
    position: 'relative',
    top: theme.optionTextTopAdjust
}));

/**
 * Represents a modal to publish an app.
 *
 * @param props The properties passed to the modal.
 */
export const PublishModal = withModalMounter((props: BasicModalProps<ConversationPublishSlotStoreData> & PublishModalDto) => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { stagingData, productionData } = props;
    const appCulture = rootStore.currentLuisAppStore?.culture;

    const [selectedOption, setSelectedOption] = React.useState<PublishSlotType>('staging');
    const [state, setState] = useUpdateState<PublishModalDto>({
        stagingData: stagingData ?? { isStaging: true, isSentimentAnalysisOn: false, isSpeechPrimingOn: false },
        productionData: productionData ?? { isStaging: false, isSentimentAnalysisOn: false, isSpeechPrimingOn: false }
    });

    const currentRegion = getOrDefaultRegionFromResource(rootStore.azureStore.session.csAccountStore);

    const options: IChoiceGroupOption[] = [
        {
            key: 'staging',
            text: localize(messages.stagingSlot),
            onRenderLabel: () => renderOption('staging', state.stagingData, localize(messages.stagingSlot))
        },
        {
            key: 'production',
            text: localize(messages.productionSlot),
            onRenderLabel: () => renderOption('production', state.productionData, localize(messages.productionSlot))
        }
    ];

    /**
     * A callback to be passed to the child components to modify the state's staging/production data
     *
     * @param newData an attribute of the staging/production data
     */
    const updateData = (isStaging: boolean, newData: ConversationPublishSlotStoreData) => {
        isStaging
            ? setState({ stagingData: { ...state.stagingData, ...newData } })
            : setState({ productionData: { ...state.productionData, ...newData } });
    };

    /**
     * On submit modal function
     */
    const onSubmit = () => {
        selectedOption === 'staging' ? props.onSubmit(state.stagingData) : props.onSubmit(state.productionData);
    };

    /**
     * Renders the slot with the given data, title, and slot type.
     *
     * @param slotType The slot type to render.
     * @param data The data to render.
     * @param titleText The text to render.
     * @returns The elements to render
     */
    const renderOption = (slotType: PublishSlotType, data: ConversationPublishSlotStoreData, titleText: string) => (
        <Option verticalAlign="center" verticalFill>
            {selectedOption === slotType ? (
                <PublishSlotCard
                    isReadOnly={false}
                    slotCardData={data}
                    onChange={updateData}
                    appCulture={appCulture}
                    currentRegion={currentRegion}
                />
            ) : (
                <OptionText>{titleText}</OptionText>
            )}
        </Option>
    );

    return (
        <BasicModal
            {...props}
            onSubmit={onSubmit}
            valid={Boolean(selectedOption)}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack styles={{ root: { padding: '2px' } }}>
                <ChoiceGroup
                    selectedKey={selectedOption}
                    options={options}
                    onChange={(_, option) => setSelectedOption(option.key as PublishSlotType)}
                />
            </Stack>
        </BasicModal>
    );
});
